import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { customersApi, deliveryAreasApi, type CustomerListItem, type DeliveryArea } from '../crmApi'
import { apiErrorMessage } from '../api'

export function Customers() {
  const navigate = useNavigate()
  const [areas, setAreas] = useState<DeliveryArea[]>([])
  const [rows, setRows] = useState<CustomerListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [areaId, setAreaId] = useState<number | ''>('')
  const [active, setActive] = useState<'' | 'true' | 'false'>('')
  const [query, setQuery] = useState('')
  const [includeDeleted, setIncludeDeleted] = useState(false)

  useEffect(() => {
    deliveryAreasApi.list().then(setAreas).catch((e) => setError(describe(e)))
  }, [])

  async function reload() {
    setLoading(true)
    try {
      setRows(await customersApi.list({
        deliveryAreaId: areaId === '' ? undefined : areaId,
        active: active === '' ? undefined : active === 'true',
        query: query.trim() || undefined,
        includeDeleted: includeDeleted || undefined,
      }))
      setError(null)
    } catch (e) {
      setError(describe(e))
    } finally {
      setLoading(false)
    }
  }

  async function restore(c: CustomerListItem) {
    try { await customersApi.restore(c.id); await reload() }
    catch (e) { setError(describe(e)) }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId, active, includeDeleted])

  return (
    <div>
      <div className="page-head">
        <h1>Kunder</h1>
        <button className="primary" onClick={() => navigate('/customers/new')}>Ny kund</button>
      </div>
      {error && <p className="error">{error}</p>}

      <div className="card filters">
        <select value={areaId} onChange={(e) => setAreaId(e.target.value === '' ? '' : Number(e.target.value))}>
          <option value="">Alla leveransområden</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={active} onChange={(e) => setActive(e.target.value as typeof active)}>
          <option value="">Aktiva och inaktiva</option>
          <option value="true">Endast aktiva</option>
          <option value="false">Endast inaktiva</option>
        </select>
        <input placeholder="Sök namn eller telefon…" value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void reload()} />
        <button onClick={() => void reload()}>Sök</button>
        <label className="inline">
          <input type="checkbox" checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)} /> Visa borttagna
        </label>
      </div>

      {loading ? <p className="muted">Laddar…</p> : (
        <table className="grid">
          <thead><tr><th>Namn</th><th>Hemnr</th><th>Mobil</th><th>Leveransområde</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className={c.isDeleted ? undefined : 'clickable'}
                onClick={() => { if (!c.isDeleted) navigate(`/customers/${c.id}`) }}>
                <td>{c.firstName} {c.lastName ?? ''}</td>
                <td>{c.homePhone}</td>
                <td>{c.mobilePhone ?? '—'}</td>
                <td>{c.deliveryAreaName || '—'}</td>
                <td>{c.isDeleted
                  ? <span className="badge muted-badge">Borttagen</span>
                  : c.isActive ? <span className="badge">Aktiv</span> : <span className="badge muted-badge">Inaktiv</span>}</td>
                <td className="actions">
                  {c.isDeleted && (
                    <button onClick={(e) => { e.stopPropagation(); void restore(c) }}>Återställ</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="muted">Inga kunder matchar.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  )
}

const describe = apiErrorMessage
